import { Download, Loader2 } from 'lucide-react';
import EmptyState from './components/EmptyState';
import ReportConfigModal from './ReportConfigModal';
import SummaryTable from './components/SummaryTable';
import React, { useRef, useState, useMemo, useEffect } from 'react';
import SalesReportTable from './components/SalesReportTable';
import MainReportHeader from './components/MainReportHeader';
import SummaryHeader from './components/SummaryHeader';
import TerritorySummaryHeader from './components/TerritorySummaryHeader';
import { buildSummaryRows } from './utils/summaryCalculations';
import { exportToExcel, exportToPDF } from './utils/reportExport';
import PurchaseReportTable from './components/PurchaseReportTable';
import TerritorySummaryTable from './components/TerritorySummaryTable';
import { buildTerritorySummary } from './utils/territorySummaryCalculations';
import { calculateSalesTotals, calculatePurchaseTotals } from './utils/calculations';
import { Box, Typography, Button, CircularProgress, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
    fetchCustomerSales,
    fetchCustomerPurchases,
    fetchCustomerBudgets,
    fetchActiveFactoryVendors,
    fetchActiveShowrooms,
    fetchTerritoryExternalBudgets,
    fetchTerritoryInternalBudgets,
    fetchProjectTotals,
    fetchAllCustomers,
    fetchActiveCustomers
} from './services/ReportDashboardService';
import { transformData } from './utils/dataMapper';

const getReportTitle = (config) => {
    const base = config.reportCategory === 'sales' ? 'Sales Report' : 'Purchase Report';
    const details = config.reportType === 'sales_report_with_details' ? ' with Project Details' : '';
    const history = config.history === 'actual_loading_date' ? ' (Actual)' : ' (Planned)';
    return `${base}${details}${history}`;
};

const ReportDashboard = () => {
    const theme = useTheme();
    const [showModal, setShowModal] = useState(true);
    const [reportConfig, setReportConfig] = useState({
        reportCategory: 'sales',
        reportType: 'sales_report',
        history: 'loading_plan_date',
        dateRange: { start: '', end: '' },
        printMainAccountOnly: 'yes',
        budgetType: 'internal',
    });

    const [appliedConfig, setAppliedConfig] = useState(null);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [expandedAccounts, setExpandedAccounts] = useState(new Set());
    const [stickySection, setStickySection] = useState('main');
    const [salesData, setSalesData] = useState([]);
    const [purchaseData, setPurchaseData] = useState([]);
    const [territoryBudgets, setTerritoryBudgets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const mainReportRef = useRef(null);
    const summaryRef = useRef(null);
    const territoryRef = useRef(null);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        const loadData = async () => {
            if (!appliedConfig) return;
            if (!appliedConfig.dateRange.start || !appliedConfig.dateRange.end) return;

            setIsLoading(true);
            setError(null);

            try {
                const fromDate = appliedConfig.dateRange.start;
                const toDate = appliedConfig.dateRange.end;
                const year = new Date(appliedConfig.dateRange.start).getFullYear();
                const currentYear = new Date().getFullYear();
                const isFutureYear = year > currentYear;
                const isSales = appliedConfig.reportCategory === 'sales';
                const dateType = appliedConfig.history === 'actual_loading_date' ? 'ACTUAL' : 'PLANNED';
                const budgetTypeParam = appliedConfig.budgetType ? appliedConfig.budgetType.toUpperCase() : 'INTERNAL';

                const territoryBudgetPromise = budgetTypeParam === 'EXTERNAL'
                    ? fetchTerritoryExternalBudgets({ year })
                    : fetchTerritoryInternalBudgets({ year });

                const customersPromise = isFutureYear
                    ? fetchActiveCustomers({ active: true, customerType: 'REGULAR' })
                    : fetchAllCustomers({ customerType: 'REGULAR' });
                const [customers, budgets, vendors, showrooms, territoryBudgetResponse, projectTotals] = await Promise.all([
                    customersPromise,
                    fetchCustomerBudgets({ year, type: budgetTypeParam }),
                    fetchActiveFactoryVendors({ active: true }),
                    fetchActiveShowrooms(),
                    territoryBudgetPromise,
                    fetchProjectTotals({ from: fromDate, to: toDate })
                ]);

                if (isSales) {
                    const sales = await fetchCustomerSales({ from: fromDate, to: toDate, dateType });
                    const formattedSales = transformData(customers, sales, budgets, vendors, showrooms, appliedConfig.budgetType, projectTotals);
                    setSalesData(formattedSales);
                } else {
                    const purchases = await fetchCustomerPurchases({ from: fromDate, to: toDate, dateType });
                    const formattedPurchases = transformData(customers, purchases, budgets, vendors, showrooms, appliedConfig.budgetType, projectTotals);
                    setPurchaseData(formattedPurchases);
                }
                setTerritoryBudgets(territoryBudgetResponse);

            } catch (err) {
                console.error("Error loading dashboard data:", err);
                setError("Failed to load report data.");
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [appliedConfig]);

    const derivedData = useMemo(() => {
        if (!appliedConfig) return [];
        return appliedConfig.reportCategory === 'sales' ? salesData : purchaseData;
    }, [appliedConfig, salesData, purchaseData]);

    const totals = useMemo(() => {
        if (!appliedConfig) return null;
        return appliedConfig.reportCategory === 'sales'
            ? calculateSalesTotals(derivedData, appliedConfig)
            : calculatePurchaseTotals(derivedData, appliedConfig);
    }, [derivedData, appliedConfig]);

    const reportTitle = useMemo(() => {
        if (!appliedConfig) return '';
        return getReportTitle(appliedConfig);
    }, [appliedConfig]);

    const summaryRows = useMemo(() => {
        if (!totals || !appliedConfig) return [];
        return buildSummaryRows(totals, appliedConfig);
    }, [totals, appliedConfig]);

    const territorySummary = useMemo(() => {
        if (!derivedData || !appliedConfig) return [];
        return buildTerritorySummary(derivedData, appliedConfig, territoryBudgets);
    }, [derivedData, appliedConfig, territoryBudgets]);

    const handleScroll = (e) => {
        const scrollTop = e.target.scrollTop;
        const summaryTop = summaryRef.current?.offsetTop ?? Infinity;
        const territoryTop = territoryRef.current?.offsetTop ?? Infinity;

        if (scrollTop >= territoryTop - 150) {
            setStickySection('territory');
        } else if (scrollTop >= summaryTop - 150) {
            setStickySection('summary');
        } else {
            setStickySection('main');
        }
    };

    const handleApplySelection = () => {
        setAppliedConfig({ ...reportConfig });
        setExpandedAccounts(new Set());
        setShowModal(false);
        setStickySection('main');
    };

    const toggleAccount = (accountId) => {
        setExpandedAccounts((prev) => {
            const next = new Set(prev);
            next.has(accountId) ? next.delete(accountId) : next.add(accountId);
            return next;
        });
    };

    const handleExportReport = (type) => {
        if (!appliedConfig) return;
        const title = getReportTitle(appliedConfig);
        const configWithTheme = { ...appliedConfig, theme };
        if (type === 'excel') exportToExcel(derivedData, `${title}.xlsx`, configWithTheme);
        if (type === 'pdf') exportToPDF(derivedData, title, configWithTheme);
    };

    const formatDate = (date) => new Date(date).toLocaleDateString('en-GB');

    if (showModal) {
        return (
            <ReportConfigModal
                reportConfig={reportConfig}
                setReportConfig={setReportConfig}
                onClose={() => setShowModal(false)}
                onApply={handleApplySelection}
            />
        );
    }

    if (!appliedConfig) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
                <EmptyState />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
            <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', p: 3, pb: 2.5 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
                    <Box>
                        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '20px' }}>
                            {reportTitle}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mt: 0.5 }}>
                            Period: {formatDate(appliedConfig.dateRange.start)} - {formatDate(appliedConfig.dateRange.end)}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, position: 'relative', zIndex: 50, width: { xs: '100%', md: 'auto' } }}>
                        <Button
                            onClick={() => setShowModal(true)}
                            variant="outlined"
                            sx={{
                                color: 'text.primary',
                                borderColor: 'divider',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 4,
                                height: 36,
                                fontSize: '13px',
                                fontWeight: 500,
                                '&:hover': { bgcolor: 'action.hover', borderColor: 'text.secondary' }
                            }}
                        >
                            Change Selection
                        </Button>
                        <Box sx={{ position: 'relative' }}>
                            <Button
                                onClick={() => setShowExportMenu(prev => !prev)}
                                variant="contained"
                                startIcon={<Download size={18} />}
                                sx={{
                                    bgcolor: 'secondary.main',
                                    color: 'secondary.contrastText',
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    px: 4,
                                    height: 36,
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    '&:hover': { bgcolor: 'secondary.dark' }
                                }}
                            >
                                Export
                            </Button>
                            {showExportMenu && (
                                <Box sx={{ position: 'absolute', right: 0, mt: 1, minWidth: 160, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '6px', boxShadow: 8, zIndex: 50, overflow: 'hidden' }}>
                                    <Button
                                        onClick={() => { handleExportReport('pdf'); setShowExportMenu(false); }}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            textTransform: 'none',
                                            color: 'text.primary',
                                            fontSize: '13px',
                                            py: 1,
                                            px: 2,
                                            borderRadius: 0,
                                            display: 'flex',
                                            textAlign: 'left',
                                            width: '100%',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                    >
                                        Export as PDF
                                    </Button>
                                    <Button
                                        onClick={() => { handleExportReport('excel'); setShowExportMenu(false); }}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            textTransform: 'none',
                                            color: 'text.primary',
                                            fontSize: '13px',
                                            py: 1,
                                            px: 2,
                                            borderRadius: 0,
                                            display: 'flex',
                                            textAlign: 'left',
                                            width: '100%',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                    >
                                        Export as Excel
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {error && (
                <Box sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.15), border: '1px solid', borderColor: 'error.main', color: 'error.main', m: 3, borderRadius: '4px', fontSize: '13px' }}>
                    {error}
                </Box>
            )}

            {isLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, gap: 1.5 }}>
                    <CircularProgress size={32} sx={{ color: 'secondary.main' }} />
                    <Typography sx={{ color: 'text.secondary', fontSize: '13px' }}>Loading Data...</Typography>
                </Box>
            ) : (
                <Box sx={{ width: '100%' }}>
                    <Box ref={scrollContainerRef} onScroll={handleScroll} sx={{ height: 'calc(100vh - 120px)', width: '100%', overflow: 'auto' }}>
                        <Box sx={{ minWidth: 1200 }}>
                            <Box ref={mainReportRef}>
                                <MainReportHeader isSticky={stickySection === 'main'} config={appliedConfig} />
                                {appliedConfig.reportCategory === 'sales' ? (
                                    <SalesReportTable
                                        data={derivedData}
                                        config={appliedConfig}
                                        totals={totals}
                                        expandedAccounts={expandedAccounts}
                                        onToggleAccount={toggleAccount}
                                    />
                                ) : (
                                    <PurchaseReportTable
                                        data={derivedData}
                                        config={appliedConfig}
                                        totals={totals}
                                        expandedAccounts={expandedAccounts}
                                        onToggleAccount={toggleAccount}
                                    />
                                )}
                            </Box>
                            <Box ref={summaryRef} sx={{ mt: 5 }}>
                                <SummaryHeader isSticky={stickySection === 'summary'} config={appliedConfig} />
                                <SummaryTable rows={summaryRows} config={appliedConfig} />
                            </Box>
                            <Box ref={territoryRef} sx={{ mt: 5, mb: 5 }}>
                                <TerritorySummaryHeader isSticky={stickySection === 'territory'} config={appliedConfig} />
                                <TerritorySummaryTable territories={territorySummary} config={appliedConfig} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ReportDashboard;